import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import DevicesTable from '../../components/tables/DevicesTable';
import DeviceVersionFields from '../../components/device/DeviceVersionFields';
import DeviceHistoryModal from '../../components/modals/DeviceHistoryModal';
import DevicePingCountModal from '../../components/modals/DevicePingCountModal';
import { getRole } from '../../utils/role';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPartners } from '../../store/features/partnersSlice';
import { fetchFieldOfficersByAdminId } from '../../store/features/fieldOfficerByPartnerSlice';
import { fetchDevices, addDevice, mapDevice, toggleDeviceStatus, fetchDeviceStatusControlFlag, toggleDeviceUpdateStatus } from '../../store/features/devicesSlice';
import axiosInstance from '../../utils/axios';

const Device = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = getRole();

  const [deviceIdSearch, setDeviceIdSearch] = useState('');
  const [debouncedDeviceIdSearch, setDebouncedDeviceIdSearch] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const emptyDeviceForm = () => ({
    device_id: '',
    partner_id: '',
    field_officer_id: '',
    hardware_id: '',
    display_software_id: '',
    device_software_id: '',
    warranty_start_date: '',
  });
  const [formData, setFormData] = useState(emptyDeviceForm());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mapping modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [mapFormData, setMapFormData] = useState({
    partner_id: '',
    field_officer_id: '',
  });
  const [mapErrors, setMapErrors] = useState({});
  const [isMapping, setIsMapping] = useState(false);

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryDevice, setSelectedHistoryDevice] = useState(null);

  // Ping count modal state
  const [showPingModal, setShowPingModal] = useState(false);
  const [selectedPingDevice, setSelectedPingDevice] = useState(null);

  // Redux state
  const { partners, status: partnersStatus } = useSelector((state) => state.partners);
  const { fieldOfficers, status: fieldOfficersStatus } = useSelector(
    (state) => state.fieldOfficerByPartner
  );
  const { partner } = useSelector((state) => state.partner);
  // const { fieldOfficerInfo } = useSelector((state) => state.fieldOfficer);
  const { devices, deviceStatusControlEnabled } = useSelector((state) => state.devices);

  // Debounce search so we don't fire a request for every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedDeviceIdSearch(deviceIdSearch.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [deviceIdSearch]);

  // Fetch devices on mount/role/search change
  useEffect(() => {
    dispatch(
      fetchDevices({
        page: 1,
        limit: 100,
        device_id: debouncedDeviceIdSearch || undefined,
      })
    );
  }, [dispatch, role, debouncedDeviceIdSearch]);

  // Fetch feature-flag once
  useEffect(() => {
    dispatch(fetchDeviceStatusControlFlag());
  }, [dispatch]);

  // Fetch partners when modal opens
  useEffect(() => {
    if (showModal) {
      dispatch(fetchPartners({ page: 1, limit: 200 }));
    }
  }, [showModal, dispatch]);

  useEffect(() => {
    if (formData.partner_id) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: formData.partner_id }));
    }
  }, [formData.partner_id, dispatch]);

  useEffect(() => {
    if (showMapModal && mapFormData.partner_id) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: mapFormData.partner_id }));
    }
  }, [showMapModal, mapFormData.partner_id, dispatch]);

  useEffect(() => {
    if (showMapModal && role === 'SUPPORT') {
      dispatch(fetchPartners({ page: 1, limit: 200 }));
    }
  }, [showMapModal, role, dispatch]);

  const handleAddDevice = () => {
    setShowModal(true);
    setFormData(emptyDeviceForm());
    setErrors({});
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData(emptyDeviceForm());
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'partner_id') next.field_officer_id = '';
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleVersionFieldsChange = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach((key) => { delete next[key]; });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate device ID (required)
    if (!formData.device_id.trim()) newErrors.device_id = 'Device ID is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      let warrantyEnd = null;
      if (formData.warranty_start_date) {
        const d = new Date(formData.warranty_start_date);
        d.setFullYear(d.getFullYear() + 1);
        warrantyEnd = d.toISOString().split('T')[0];
      }

      await dispatch(addDevice({
        device_id: formData.device_id,
        partner_id: formData.partner_id || null,
        field_officer_id: formData.field_officer_id || null,
        hardware_id: formData.hardware_id || null,
        display_software_id: formData.display_software_id || null,
        device_software_id: formData.device_software_id || null,
        warranty_start_date: formData.warranty_start_date || null,
        warranty_end_date: warrantyEnd,
      })).unwrap();

      // Refresh devices list
      dispatch(
        fetchDevices({
          page: 1,
          limit: 100,
          device_id: debouncedDeviceIdSearch || undefined,
        })
      );
      handleClose();
    } catch (error) {
      console.error('Error adding device:', error);
      setErrors({ submit: error || 'Failed to add device. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeviceClick = (device) => {
    if (!device?.device_id) return;
    navigate(`/device/${device.device_id}`, { state: { device } });
  };

  const handleMapClick = (device) => {
    setSelectedDevice(device);
    setShowMapModal(true);

    // For PARTNER, auto-set their partner_id and fetch their field officers
    if ((role === 'PARTNER' || role === 'TEAM_MEMBER') && partner?.admin_id) {
      setMapFormData({
        partner_id: partner.admin_id, // Auto-set to current partner
        field_officer_id: '',
      });
      // Fetch field officers for this partner
      dispatch(fetchFieldOfficersByAdminId({ admin_id: partner.admin_id }));
    } else {
      // For SUPPORT, show partner dropdown
      setMapFormData({
        partner_id: '',
        field_officer_id: '',
      });
    }

    setMapErrors({});
  };

  const handleMapModalClose = () => {
    setShowMapModal(false);
    setSelectedDevice(null);
    setMapFormData({
      partner_id: '',
      field_officer_id: '',
    });
    setMapErrors({});
  };

  const handleHistoryClick = (device) => {
    setSelectedHistoryDevice(device);
    setShowHistoryModal(true);
  };

  const handleHistoryModalClose = () => {
    setShowHistoryModal(false);
    setSelectedHistoryDevice(null);
  };

  const handlePingCountClick = (device) => {
    setSelectedPingDevice(device);
    setShowPingModal(true);
  };

  const handlePingModalClose = () => {
    setShowPingModal(false);
    setSelectedPingDevice(null);
  };

  const handleToggleStatus = async (device) => {
    const action = device.device_status === false ? 'enable' : 'disable';
    if (!window.confirm(`Are you sure you want to ${action} device "${device.device_id}"?`)) return;
    try {
      await dispatch(toggleDeviceStatus(device.device_id)).unwrap();
    } catch (error) {
      console.error('Error toggling device status:', error);
      alert('Failed to update device status. Please try again.');
    }
  };

  const handleToggleUpdateStatus = async (device) => {
    const action = device.auto_update_enabled ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} auto-update for device "${device.device_id}"?`)) return;
    try {
      await dispatch(toggleDeviceUpdateStatus(device.device_id)).unwrap();
    } catch (error) {
      console.error('Error toggling auto-update status:', error);
      alert('Failed to update device auto-update status. Please try again.');
    }
  };

  // ── Location modal (PARTNER only) ──────────────────────────────────────────
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationDevice, setLocationDevice]       = useState(null);
  const [detectedCoords, setDetectedCoords]       = useState(null);
  const [detecting, setDetecting]                 = useState(false);
  const [locationSaving, setLocationSaving]       = useState(false);
  const [locationSuccess, setLocationSuccess]     = useState(false);
  const [locationError, setLocationError]         = useState('');

  const handleLocationClick = (device) => {
    setLocationDevice(device);
    setDetectedCoords(null);
    setLocationSuccess(false);
    setLocationError('');
    setShowLocationModal(true);
  };

  const handleLocationModalClose = () => {
    setShowLocationModal(false);
    setLocationDevice(null);
    setDetectedCoords(null);
    setLocationSuccess(false);
    setLocationError('');
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setDetecting(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDetectedCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setDetecting(false);
      },
      () => {
        setLocationError('Unable to retrieve your location. Please allow location access and try again.');
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleLocationSubmit = async () => {
    if (!detectedCoords) {
      setLocationError('Please detect your current location first.');
      return;
    }
    setLocationSaving(true);
    setLocationError('');
    try {
      await axiosInstance.post('/partner/device-location', {
        device_id: locationDevice.device_id,
        latitude:  detectedCoords.lat,
        longitude: detectedCoords.lng,
      });
      setLocationSuccess(true);
    } catch (err) {
      setLocationError(err.response?.data?.msg || 'Failed to save location. Please try again.');
    } finally {
      setLocationSaving(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const handleMapInputChange = (e) => {
    const { name, value } = e.target;
    setMapFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'partner_id') next.field_officer_id = '';
      return next;
    });
    if (mapErrors[name]) {
      setMapErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleMapSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (Object.keys(newErrors).length > 0) {
      setMapErrors(newErrors);
      return;
    }

    setIsMapping(true);
    try {
      await dispatch(mapDevice({
        device_id: selectedDevice.device_id,
        partner_id: mapFormData.partner_id || null,
        field_officer_id: mapFormData.field_officer_id || null,
      })).unwrap();

      // Refresh devices list
      dispatch(
        fetchDevices({
          page: 1,
          limit: 100,
          device_id: debouncedDeviceIdSearch || undefined,
        })
      );
      handleMapModalClose();
    } catch (error) {
      console.error('Error mapping device:', error);
      setMapErrors({ submit: error || 'Failed to map device. Please try again.' });
    } finally {
      setIsMapping(false);
    }
  };

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex flex-column flex-md-row justify-content-between mb-3 w-100">
          <h1 className="fw-bolder">Devices</h1>
          <div className="d-flex justify-content-end align-items-center mt-3 m-md-0">
            {role === 'SUPPORT' && (
              <Button
                variant="dark"
                className="ms-2"
                onClick={handleAddDevice}
              >
                <FaPlus className="me-2" />
                Add Device
              </Button>
            )}
          </div>
        </div>

        <div className="mb-3">
          <Form className="d-flex gap-2 align-items-center" onSubmit={(e) => e.preventDefault()}>
            <Form.Control
              type="search"
              value={deviceIdSearch}
              onChange={(e) => setDeviceIdSearch(e.target.value)}
              placeholder="Search Device ID, Partner, or Field Officer"
              aria-label="Search Device ID, Partner, or Field Officer"
            />
            {deviceIdSearch.trim().length > 0 && (
              <Button
                variant="outline-secondary"
                onClick={() => setDeviceIdSearch('')}
              >
                Clear
              </Button>
            )}
          </Form>
        </div>

        <DevicesTable
          devices={devices}
          onDeviceClick={handleDeviceClick}
          onMapClick={handleMapClick}
          onHistoryClick={handleHistoryClick}
          onPingCountClick={handlePingCountClick}
          onToggleStatus={handleToggleStatus}
          onLocationClick={handleLocationClick}
          deviceStatusControlEnabled={deviceStatusControlEnabled}
          onToggleUpdateStatus={handleToggleUpdateStatus}
          role={role}
        />

        {/* Add Device Modal */}
        <Modal show={showModal} onHide={handleClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Add Device</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {errors.submit && (
                <Alert variant="danger" className="mb-3">
                  {errors.submit}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>
                  Device ID <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="device_id"
                  value={formData.device_id}
                  onChange={handleInputChange}
                  placeholder="Enter Device ID"
                  isInvalid={!!errors.device_id}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.device_id}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Partner Name (Optional)</Form.Label>
                <Form.Select
                  name="partner_id"
                  value={formData.partner_id}
                  onChange={handleInputChange}
                >
                  <option value="">Select Partner</option>
                  {partnersStatus === 'loading' ? (
                    <option disabled>Loading partners...</option>
                  ) : (
                    partners.map((partner) => (
                      <option key={partner.admin_id} value={partner.admin_id}>
                        {partner.entity_name}
                      </option>
                    ))
                  )}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Field Officer (Optional)</Form.Label>
                <Form.Select
                  name="field_officer_id"
                  value={formData.field_officer_id}
                  onChange={handleInputChange}
                  disabled={!formData.partner_id || fieldOfficersStatus === 'loading'}
                >
                  <option value="">
                    {!formData.partner_id
                      ? 'Select Partner first'
                      : fieldOfficersStatus === 'loading'
                        ? 'Loading field officers...'
                        : 'Select Field Officer'}
                  </option>
                  {formData.partner_id &&
                    fieldOfficersStatus === 'succeeded' &&
                    fieldOfficers.map((fieldOfficer) => (
                      <option
                        key={fieldOfficer.field_officer_id}
                        value={fieldOfficer.field_officer_id}
                      >
                        {fieldOfficer.name}
                      </option>
                    ))}
                </Form.Select>
                {formData.partner_id && fieldOfficers.length === 0 && fieldOfficersStatus === 'succeeded' && (
                  <Form.Text className="text-muted">
                    No field officers found for this partner.
                  </Form.Text>
                )}
              </Form.Group>

              <DeviceVersionFields
                hardwareId={formData.hardware_id}
                displaySoftwareId={formData.display_software_id}
                deviceSoftwareId={formData.device_software_id}
                onChange={handleVersionFieldsChange}
                hardwareError={errors.hardware_id}
                displayError={errors.display_software_id}
                firmwareError={errors.device_software_id}
              />

              <Form.Group className="mb-3 mt-3">
                <Form.Label>Warranty Start Date <span className="text-muted">(Optional)</span></Form.Label>
                <Form.Control
                  type="date"
                  name="warranty_start_date"
                  value={formData.warranty_start_date}
                  onChange={handleInputChange}
                />
                {formData.warranty_start_date && (
                  <Form.Text className="text-muted">
                    Warranty end will be auto-set to{' '}
                    {(() => {
                      const d = new Date(formData.warranty_start_date);
                      d.setFullYear(d.getFullYear() + 1);
                      return d.toLocaleDateString();
                    })()}
                  </Form.Text>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Adding...
                  </>
                ) : (
                  'Add Device'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Map Device Modal */}
        <Modal show={showMapModal} onHide={handleMapModalClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Map Device to Partner</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleMapSubmit}>
            <Modal.Body>
              {mapErrors.submit && (
                <Alert variant="danger" className="mb-3">
                  {mapErrors.submit}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Device ID</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedDevice?.device_id || ''}
                  readOnly
                  disabled
                  className="bg-light"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Partner Name</Form.Label>
                {(role === 'PARTNER' || role === 'TEAM_MEMBER') ? (
                  <Form.Control
                    type="text"
                    value={partner?.entity_name || 'Current Partner'}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                ) : (
                  <Form.Select
                    name="partner_id"
                    value={mapFormData.partner_id}
                    onChange={handleMapInputChange}
                  >
                    <option value="">Select Partner</option>
                    {partnersStatus === 'loading' ? (
                      <option disabled>Loading partners...</option>
                    ) : (
                      partners.map((partner) => (
                        <option key={partner.admin_id} value={partner.admin_id}>
                          {partner.entity_name}
                        </option>
                      ))
                    )}
                  </Form.Select>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Field Officer (Optional)</Form.Label>
                <Form.Select
                  name="field_officer_id"
                  value={mapFormData.field_officer_id}
                  onChange={handleMapInputChange}
                  disabled={!mapFormData.partner_id || fieldOfficersStatus === 'loading'}
                >
                  <option value="">
                    {!mapFormData.partner_id
                      ? 'Select Partner first'
                      : fieldOfficersStatus === 'loading'
                        ? 'Loading field officers...'
                        : 'Select Field Officer'}
                  </option>
                  {mapFormData.partner_id &&
                    fieldOfficersStatus === 'succeeded' &&
                    fieldOfficers.map((fieldOfficer) => (
                      <option
                        key={fieldOfficer.field_officer_id}
                        value={fieldOfficer.field_officer_id}
                      >
                        {fieldOfficer.name}
                      </option>
                    ))}
                </Form.Select>
                {mapFormData.partner_id && fieldOfficers.length === 0 && fieldOfficersStatus === 'succeeded' && (
                  <Form.Text className="text-muted">
                    No field officers found for this partner.
                  </Form.Text>
                )}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleMapModalClose} disabled={isMapping}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isMapping}>
                {isMapping ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Mapping...
                  </>
                ) : (
                  'Map Device'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Device History Modal */}
        <DeviceHistoryModal
          show={showHistoryModal}
          onHide={handleHistoryModalClose}
          device={selectedHistoryDevice}
        />

        {/* Device Ping Count Modal */}
        <DevicePingCountModal
          show={showPingModal}
          onHide={handlePingModalClose}
          device={selectedPingDevice}
        />

        {/* Set Device Location Modal (PARTNER) */}
        <Modal show={showLocationModal} onHide={handleLocationModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Set Device Location</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {locationDevice && (
              <p className="text-muted mb-3">
                Device: <strong>{locationDevice.device_id}</strong>
              </p>
            )}
            {locationSuccess ? (
              <Alert variant="success">Location saved successfully!</Alert>
            ) : (
              <>
                {locationError && <Alert variant="danger">{locationError}</Alert>}
                <div className="d-flex flex-column align-items-center gap-3">
                  <Button
                    variant="outline-primary"
                    onClick={handleDetectLocation}
                    disabled={detecting}
                  >
                    {detecting ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Detecting…</>
                    ) : 'Get Current Location'}
                  </Button>
                  {detectedCoords && (
                    <Alert variant="info" className="w-100 text-center mb-0">
                      <strong>Detected:</strong><br />
                      Lat: {detectedCoords.lat.toFixed(6)}<br />
                      Lng: {detectedCoords.lng.toFixed(6)}
                    </Alert>
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          {!locationSuccess && (
            <Modal.Footer>
              <Button variant="secondary" onClick={handleLocationModalClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleLocationSubmit}
                disabled={!detectedCoords || locationSaving}
              >
                {locationSaving ? <Spinner animation="border" size="sm" /> : 'Save Location'}
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      </div>
    </Wrapper>
  );
};

export default Device;
